/**
 * Helpdesk Lead landing — /app/landing/helpdesk
 *
 * Tier 1/2 support: service desk, end-user issues, identity & access.
 * Hero tiles surface MFA enrolment, risky sign-ins, leavers awaiting
 * offboarding, open compliance auto-tickets, on-call shifts today, and
 * stale credentials. Empty states are graceful — endpoints that don't
 * exist yet (e.g. /api/idp-risky) just render "no data yet".
 */
import { useEffect, useState, useCallback } from 'react'
import {
  LifeBuoy, UserMinus, Phone, KeyRound, AlertCircle,
} from 'lucide-react'
import { api } from '../lib/api'
import {
  LandingHeader, HeroTile, TwoColumnBody, Panel, EmptyState,
  StickyFooter, ListRow, safeNum,
} from '../components/landing/LandingShell'

interface User {
  id: string
  email?: string
  display_name?: string
  is_human?: boolean
  totp_secret?: string | null
  webauthn?: unknown
  mfa_enrolled?: boolean
}
interface RiskySignin { id: string; user?: string; reason?: string; created_at?: string; severity?: string }
interface JmlItem { id: string; user?: string; kind?: string; state?: string; due_at?: string }
interface Ticket { id: string; title?: string; rule?: string; severity?: string }
interface OncallShift { id: string; team?: string; user?: string; start_at?: string; end_at?: string }
interface Credential { id: string; name?: string; expires_at?: string; days_until_expiry?: number }

export default function HelpdeskLandingPage() {
  const [refreshing, setRefreshing] = useState(false)
  const [users, setUsers]           = useState<User[]>([])
  const [risky, setRisky]           = useState<RiskySignin[]>([])
  const [leavers, setLeavers]       = useState<JmlItem[]>([])
  const [tickets, setTickets]       = useState<Ticket[]>([])
  const [shifts, setShifts]         = useState<OncallShift[]>([])
  const [staleCreds, setStaleCreds] = useState<Credential[]>([])

  const load = useCallback(() => {
    setRefreshing(true)
    const j = (p: Promise<any>) => p.catch(() => ({ data: null }))
    Promise.all([
      j(api.get('/api/cdm/users', { params: { is_human: true, limit: 500 } })),
      j(api.get('/api/idp-risky', { params: { state: 'open', limit: 10 } })),
      j(api.get('/api/cdm/jml', { params: { kind: 'leaver', state: 'pending', limit: 10 } })),
      j(api.get('/api/cdm/auto-ticket/tickets', { params: { state: 'open' } })),
      j(api.get('/api/oncall', { params: { window: 'today' } })),
      j(api.get('/api/credentials')),
    ]).then(([u, r, l, t, o, c]) => {
      setUsers(u.data?.users || u.data?.results || [])
      setRisky(r.data?.signins || r.data?.results || r.data?.items || [])
      setLeavers(l.data?.items || l.data?.results || [])
      setTickets(t.data?.tickets || t.data?.results || [])
      setShifts(o.data?.shifts || o.data?.results || o.data?.items || [])
      const creds = (c.data?.credentials || c.data?.results || c.data?.items || []) as Credential[]
      // Stale = expiring in <30 days OR already expired.
      setStaleCreds(creds.filter(cr => (cr.days_until_expiry ?? 999) < 30))
    }).finally(() => setRefreshing(false))
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  const humans = users.filter(u => u.is_human !== false)
  const enrolled = humans.filter(u =>
    u.mfa_enrolled === true || !!u.totp_secret || !!u.webauthn,
  ).length
  const mfaPct = humans.length === 0 ? null : (enrolled / humans.length) * 100

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      <LandingHeader
        title="Helpdesk Landing"
        subtitle="MFA · risky sign-ins · JML · on-call · credentials"
        persona="helpdesk"
        onRefresh={load}
        refreshing={refreshing}
      />

      {/* Hero — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroTile label="MFA enrolment"
          value={mfaPct != null ? `${mfaPct.toFixed(0)}%` : '—'}
          tone={mfaPct == null ? 'muted' : mfaPct >= 95 ? 'emerald' : mfaPct >= 80 ? 'amber' : 'rose'}
          sub={humans.length > 0 ? `${enrolled}/${humans.length}` : ''}
          to="/app/cdm/users" />
        <HeroTile label="Risky sign-ins" value={safeNum(risky.length)}
          tone={risky.length > 0 ? 'amber' : 'emerald'} to="/app/idp-risky" />
        <HeroTile label="Leavers pending" value={safeNum(leavers.length)}
          tone={leavers.length > 0 ? 'amber' : 'emerald'} to="/app/admin/jml" />
        <HeroTile label="Open auto-tickets" value={safeNum(tickets.length)}
          tone={tickets.length > 0 ? 'amber' : 'emerald'} to="/app/cdm/remediation" />
        <HeroTile label="On-call today" value={safeNum(shifts.length)}
          tone={shifts.length > 0 ? 'cyan' : 'muted'} to="/app/oncall" />
        <HeroTile label="Stale creds" value={safeNum(staleCreds.length)}
          tone={staleCreds.length > 0 ? 'amber' : 'emerald'} to="/app/credentials" />
      </div>

      <TwoColumnBody
        left={(
          <>
            <Panel title="JML pending · leavers" viewAllTo="/app/admin/jml">
              {leavers.length === 0 ? (
                <EmptyState message="No leavers awaiting offboarding." />
              ) : (
                <div className="space-y-1">
                  {leavers.slice(0, 6).map(l => (
                    <ListRow key={l.id}
                      to="/app/admin/jml"
                      primary={l.user || l.id}
                      secondary={l.kind}
                      right={l.due_at?.slice(0, 10)} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Auto-tickets needing handoff" viewAllTo="/app/cdm/remediation">
              {tickets.length === 0 ? (
                <EmptyState message="No open auto-tickets." />
              ) : (
                <div className="space-y-1">
                  {tickets.slice(0, 6).map(t => (
                    <ListRow key={t.id}
                      primary={t.title || t.rule || t.id}
                      secondary={t.rule}
                      right={<span className={
                        t.severity === 'critical' ? 'text-rose-light'
                          : t.severity === 'high' ? 'text-amber-light' : 'text-text-secondary'
                      }>{t.severity || 'open'}</span>} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
        right={(
          <>
            <Panel title="Risky sign-ins" viewAllTo="/app/idp-risky">
              {risky.length === 0 ? (
                <EmptyState message="No risky sign-ins reported."
                  configureTo="/app/connectors-manage" configureLabel="configure IdP feed" />
              ) : (
                <div className="space-y-1">
                  {risky.slice(0, 6).map(r => (
                    <ListRow key={r.id}
                      to="/app/idp-risky"
                      primary={r.user || r.id}
                      secondary={r.reason}
                      right={<span className={r.severity === 'critical' ? 'text-rose-light' : 'text-amber-light'}>
                        {r.severity || 'risky'}
                      </span>} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Stale credentials · rotation needed" viewAllTo="/app/credentials">
              {staleCreds.length === 0 ? (
                <EmptyState message="All credentials within rotation policy." />
              ) : (
                <div className="space-y-1">
                  {staleCreds.slice(0, 6).map(c => {
                    const days = c.days_until_expiry ?? 999
                    return (
                      <ListRow key={c.id}
                        to="/app/credentials"
                        primary={c.name || c.id}
                        secondary={c.expires_at?.slice(0, 10)}
                        right={<span className={days < 0 ? 'text-rose-light' : days < 7 ? 'text-rose-light' : 'text-amber-light'}>
                          {days < 0 ? 'expired' : `${days}d`}
                        </span>} />
                    )
                  })}
                </div>
              )}
            </Panel>
          </>
        )}
      />

      <StickyFooter actions={[
        { label: 'JML inbox',     icon: UserMinus,   tone: 'primary',   to: '/app/admin/jml' },
        { label: 'On-call',       icon: Phone,       tone: 'secondary', to: '/app/oncall' },
        { label: 'IdP risky',     icon: AlertCircle, tone: 'ghost',     to: '/app/idp-risky' },
        { label: 'Credentials',   icon: KeyRound,    tone: 'ghost',     to: '/app/credentials' },
      ]} />

      <span className="hidden"><LifeBuoy /></span>
    </div>
  )
}
